import {AiToolImpl} from "@me8eon/aiclient";
import {mustBeObjectWithFields, mustBeString} from "@me8eon/validate";
import {ElasticSearchConfig, ElasticSearchHit, ElasticSearchKnnSearchResponse} from "@me8eon/ai.tools.utils";

/**
 * Converts an Elasticsearch hit to a formatted string.
 * @param hit
 * @param index
 */
const hitToString = (hit: ElasticSearchHit, index: number): string => {
    return `**Document ${index + 1}**:
     ${hit._source.title && `- Title: ${hit._source.title}\n`}
     - Score: ${hit._score}, 
     - Index: ${hit._index}, 
     - ID: ${hit._id}
     - Source: """${JSON.stringify(hit._source)}"""`;
};

type ToolArgs = {
    query: string
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (config: ElasticSearchConfig) => ToolImpl

/**
 * Hybrid semantic search optimised for Nautilus (and similar indices where documents have
 * short body content but meaningful titles).
 *
 * Uses a linear retriever that combines:
 *   - ELSER sparse-vector match  (weight 1.0)
 *   - e5-small dense-vector match (weight 1.0)
 *   - title keyword match         (weight 3.0)  ← boosted to surface title-heavy pages
 *
 * Results are re-ranked by a cross-encoder before being returned.
 */
export const NautilusHybridSemanticSearchToolImpl: Tool = (esConfig) => {
    return {
        type: "nautilusSemanticHybridSearch",
        toolDefn: {
            type: 'function',
            name: "nautilusSemanticHybridSearch",
            description: "This tool performs a hybrid semantic search on specified Elasticsearch indices using semantic_text fields - one field with vector (e5-small), another with sparse (elser), and a title field with boosted keyword matching. It is optimised for content where pages have short body text but meaningful titles.",
            parameters: {
                type: "object",
                required: ["query"],
                additionalProperties: false,
                properties: {
                    query: {
                        type: "string",
                        description: "The search query string used to find relevant documents. Should be a concise and clear representation of the information being sought. This query HAS TO BE IN ENGLISH as the underlying models are trained primarily on English data.",
                    }
                },
            },
        },
        validateArgs: mustBeObjectWithFields({query: mustBeString}),
        execute: async (indices, input) => {
            try {
                const {
                    elasticSearchUrl,
                    axios,
                    tokenFn,
                    blacklistedFields = [],
                } = esConfig;
                const {query} = input;
                if (indices.length === 0) throw new Error("No indices provided to nautilusSemanticHybridSearch tool");
                const top = 5;
                const indicesAsString = indices.join(",");
                const url = `${elasticSearchUrl}/${indicesAsString}/_search`;
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: `ApiKey ${await tokenFn()}`,
                };

                const body = {
                    "_source": {
                        "excludes": [...blacklistedFields,
                            "denied_countries",
                            "denied_companies",
                            "denied_users",
                            "allowed_countries",
                            "allowed_companies",
                            "allowed_users",
                            "allowed_all_countries",
                            "allowed_all_companies",
                            "allowed_all_users",
                            "semantic_test",
                            "dense_vector",
                            "sparse_vector"],
                    },
                    "size": top,
                    "retriever": {
                        "text_similarity_reranker": {
                            "retriever": {
                                "linear": {
                                    "rank_window_size": 100,
                                    "normalizer": "minmax",
                                    "retrievers": [
                                        {
                                            "retriever": {
                                                "standard": {
                                                    "query": {
                                                        "match": {
                                                            "sparse_vector": query,
                                                        }
                                                    }
                                                }
                                            },
                                            "weight": 1.0,
                                            "normalizer": "minmax"
                                        },
                                        {
                                            "retriever": {
                                                "standard": {
                                                    "query": {
                                                        "match": {
                                                            "dense_vector": query,
                                                        }
                                                    }
                                                }
                                            },
                                            "weight": 1.0,
                                            "normalizer": "minmax"
                                        },
                                        {
                                            "retriever": {
                                                "standard": {
                                                    "query": {
                                                        "match": {
                                                            "title": query,
                                                        }
                                                    }
                                                }
                                            },
                                            "weight": 3.0,
                                            "normalizer": "minmax"
                                        }
                                    ]
                                }
                            },
                            "field": "semantic_text",
                            "inference_id": "cross-encoder-rerank",
                            "inference_text": query,
                            "rank_window_size": 5
                        },
                    },
                    "highlight": {
                        "fields": {
                            "sparse_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            },
                            "dense_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            }
                        }
                    }
                };

                const response = await axios.post<ElasticSearchKnnSearchResponse>(url, body, {headers: headers});
                const topDocuments = response.data?.hits?.hits?.slice(0, top).map((hit, index) => hitToString(hit, index));
                const content = `Here are the top ${topDocuments.length} documents found:\n\n${topDocuments.join("\n\n")}`;
                return {
                    value: content,
                }

            } catch (e: any) {
                return {
                    value: "An error occurred while performing the Nautilus hybrid semantic search.",
                    _meta: {
                        errorMessage: String(e?.message ?? e),
                    }
                };
            }
        },
    };
};
